import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import MasterAccounts from "@/pages/MasterAccounts";
import Attendance from "@/pages/Attendance";
import Schoolgram from "@/pages/Schoolgram";
import Inventory from "@/pages/Inventory";
import Assignments from "@/pages/Assignments";
import Quizzes from "@/pages/Quizzes";
import VortexGuard from "@/pages/VortexGuard";
import Guests from "@/pages/Guests";
import IzinKeluar from "@/pages/IzinKeluar";
import TeacherAttendance from "@/pages/TeacherAttendance";
import PublicOrg from "@/pages/PublicOrg";
import Documentation from "@/pages/Documentation";
import Presentation from "@/pages/Presentation";
import PublicGallery from "@/pages/PublicGallery";
import PublicProfile from "@/pages/PublicProfile";
import SocialFund from "@/pages/SocialFund";
import Elections from "@/pages/Elections";
import Achievements from "@/pages/Achievements";
import Announcements from "@/pages/Announcements";
import Feedback from "@/pages/Feedback";
import Analytics from "@/pages/Analytics";
import ForgotPassword from "@/pages/ForgotPassword";
import ChangePassword from "@/pages/ChangePassword";
import ResetRequests from "@/pages/ResetRequests";
import PrintCards from "@/pages/PrintCards";
import Reports from "@/pages/Reports";
import Calendar from "@/pages/Calendar";
import PpdbPublic from "@/pages/PpdbPublic";
import AdminPpdb from "@/pages/AdminPpdb";
import SettingsPage from "@/pages/SettingsPage";
import MyCard from "@/pages/MyCard";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import SchoolInfo from "@/pages/SchoolInfo";
import OrgStructure from "@/pages/OrgStructure";
import OrgStructureEditor from "@/pages/OrgStructureEditor";
import Library from "@/pages/Library";
import AttendanceRecap from "@/pages/AttendanceRecap";
import AttendanceConfirmed from "@/pages/AttendanceConfirmed";
import Jadwal from "@/pages/Jadwal";
import Leaderboard from "@/pages/Leaderboard";
import GuruStaff from "@/pages/GuruStaff";
import Profile from "@/pages/Profile";
import Piket from "@/pages/Piket";
import FaceAttendance from "@/pages/FaceAttendance";
import GateMonitor from "@/pages/GateMonitor";
import FaceEnroll from "@/pages/FaceEnroll";
import PiketTV from "@/pages/PiketTV";
import ResetPassword from "@/pages/ResetPassword";
import GuestInfo from "@/pages/GuestInfo";
import DailyAttendance from "@/pages/DailyAttendance";
import QuestionBank from "@/pages/QuestionBank";
import LiveMonitor from "@/pages/LiveMonitor";
import ExamRequests from "@/pages/ExamRequests";
import "@/index.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-400">Memuat...</div></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function AppInner() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors/>
      <Routes>
        <Route path="/login" element={<Login/>}/>
        <Route path="/forgot-password" element={<ForgotPassword/>}/>
        <Route path="/reset-password" element={<ResetPassword/>}/>
        <Route path="/piket/tv" element={<Protected><PiketTV/></Protected>}/>
        <Route path="/change-password" element={<ChangePassword/>}/>
        <Route path="/ppdb" element={<PpdbPublic/>}/>
        <Route path="/struktur-organisasi" element={<PublicOrg/>}/>
        <Route path="/dokumentasi" element={<Documentation/>}/>
        <Route path="/presentasi" element={<Presentation/>}/>
        <Route path="/galeri" element={<PublicGallery/>}/>
        <Route path="/profil-sekolah" element={<PublicProfile/>}/>
        <Route path="/konfirmasi-absensi" element={<AttendanceConfirmed/>}/>
        <Route path="/info-tamu" element={<GuestInfo/>}/>
        <Route path="/" element={<Protected><DashboardLayout/></Protected>}>
          <Route index element={<Dashboard/>}/>
          <Route path="accounts" element={<MasterAccounts/>}/>
          <Route path="analytics" element={<Analytics/>}/>
          <Route path="attendance" element={<Attendance/>}/>
          <Route path="schoolgram" element={<Schoolgram/>}/>
          <Route path="inventory" element={<Inventory/>}/>
          <Route path="assignments" element={<Assignments/>}/>
          <Route path="quizzes" element={<Quizzes/>}/>
          <Route path="vortex-guard" element={<VortexGuard/>}/>
          <Route path="guests" element={<Guests/>}/>
          <Route path="izin-keluar" element={<IzinKeluar/>}/>
          <Route path="teacher-attendance" element={<TeacherAttendance/>}/>
          <Route path="absensi-harian" element={<DailyAttendance/>}/>
          <Route path="bank-soal" element={<QuestionBank/>}/>
          <Route path="pemantau-live" element={<LiveMonitor/>}/>
          <Route path="pengajuan-ujian" element={<ExamRequests/>}/>
          <Route path="classes" element={<Classes/>}/>
          <Route path="classes/:id" element={<ClassDetail/>}/>
          <Route path="school-info" element={<SchoolInfo/>}/>
          <Route path="org-structure" element={<OrgStructure/>}/>
          <Route path="org-structure/:id" element={<OrgStructureEditor/>}/>
          <Route path="uang-kas" element={<Navigate to="/classes" replace/>}/>
          <Route path="social-fund" element={<SocialFund/>}/>
          <Route path="elections" element={<Elections/>}/>
          <Route path="achievements" element={<Achievements/>}/>
          <Route path="announcements" element={<Announcements/>}/>
          <Route path="feedback" element={<Feedback/>}/>
          <Route path="reports" element={<Reports/>}/>
          <Route path="calendar" element={<Calendar/>}/>
          <Route path="admin-ppdb" element={<AdminPpdb/>}/>
          <Route path="settings" element={<SettingsPage/>}/>
          <Route path="reset-requests" element={<ResetRequests/>}/>
          <Route path="my-card" element={<MyCard/>}/>
          <Route path="library" element={<Library/>}/>
          <Route path="attendance-recap" element={<AttendanceRecap/>}/>
          <Route path="jadwal" element={<Jadwal/>}/>
          <Route path="leaderboard" element={<Leaderboard/>}/>
          <Route path="guru-staff" element={<GuruStaff/>}/>
          <Route path="profile" element={<Profile/>}/>
          <Route path="piket" element={<Piket/>}/>
          <Route path="face-attendance" element={<FaceAttendance/>}/>
          <Route path="face-enroll" element={<FaceEnroll/>}/>
          <Route path="gate-monitor" element={<GateMonitor/>}/>
        </Route>
        <Route path="/print-cards" element={<Protected><PrintCards/></Protected>}/>
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return <AuthProvider><SettingsProvider><ThemeProvider><AppInner/></ThemeProvider></SettingsProvider></AuthProvider>;
}
